import { Fragment, useState, useMemo } from 'react';
import ChevronRightSmallSolid from '@simplywallst/ui-core/icons/ChevronRightSmallSolid';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import {
  StyledTable,
  TableHeadRow,
  TableHeadCell,
  ScrollPane,
  CtaButton,
} from './styled';
import { HoldingRow } from './HoldingRow';
import type {
  Period,
  DividendHoldingItem,
} from '@/features/portfolio/types/portfolioDividend';
import { extractDividendQualityByPeriod } from '@/features/portfolio/utils/extractPorfolioDividendData';
import { getHeaders } from './utils';
import { useCollapsibleList } from './hooks/useCollapsibleList';
import { Button, Text, Box } from '@simplywallst/ui-core';
import { usePortfolioDividend } from '../../hooks/usePortfolioDividend';
import { usePortfolioHoldingsDividendForecast } from '../../hooks/usePortfolioHoldingsDividendForecast';
import { useReduxStateForPortfolio } from '../../hooks/useReduxStateForPortfolio';
import { useTracking } from 'react-tracking';

interface Props {
  selectedPeriod: Period;
}

export const DividendQualityForecastTable = ({ selectedPeriod }: Props) => {
  const [isSticky, setIsSticky] = useState(false);
  const { trackEvent } = useTracking({
    type: 'track',
  });

  const { portfolioId } = useReduxStateForPortfolio();
  const { data: portfolioDividend } = usePortfolioDividend({ portfolioId });
  const { data: portfolioDividendHoldingForecast } =
    usePortfolioHoldingsDividendForecast({ portfolioId });

  const dividendData = useMemo(() => {
    return extractDividendQualityByPeriod(portfolioDividendHoldingForecast)[
      selectedPeriod
    ];
  }, [portfolioDividendHoldingForecast, selectedPeriod]);

  const holdings = useMemo(() => {
    const holdings = dividendData?.holdings || [];
    const excludedHoldings = dividendData?.excludedHoldings || [];
    return [...holdings, ...excludedHoldings];
  }, [dividendData?.excludedHoldings, dividendData?.holdings]);

  const {
    itemList: holdingsList,
    isCollapsed: hasCollapsedHoldings,
    showAllItems: showAllHoldings,
  } = useCollapsibleList(holdings);

  const headers = useMemo(() => getHeaders(selectedPeriod), [selectedPeriod]);

  const cellContents = useMemo(
    () =>
      headers.map((header) => {
        const cellLines = header.split('\n');
        return cellLines.map((line, index) => (
          <Fragment key={index}>
            {line}
            <br />
          </Fragment>
        ));
      }),
    [headers]
  );

  const [firstHeadCell, ...restHeadCells] = cellContents;

  return (
    <ScrollPane>
      <StyledTable>
        <thead>
          <TableHeadRow>
            <IntersectionObserver
              threshold={[1]}
              onChange={({ intersectionRatio }) => {
                setIsSticky(intersectionRatio < 1);
              }}
            >
              <TableHeadCell isSticky={isSticky}>{firstHeadCell}</TableHeadCell>
            </IntersectionObserver>
            {restHeadCells.map((cell, index) => (
              <TableHeadCell key={index}>{cell}</TableHeadCell>
            ))}
          </TableHeadRow>
        </thead>
        <tbody>
          {holdingsList.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <Box backgroundColor="phantom" padding="x2">
                  <Text
                    typography="xSmall"
                    color="textSubtle"
                    textAlign="center"
                  >
                    You currently have no holdings that pay dividends
                  </Text>
                </Box>
              </td>
            </tr>
          ) : (
            <>
              {holdingsList.map((holding: DividendHoldingItem) => (
                <HoldingRow
                  key={holding.tickerSymbol}
                  holding={holding}
                  isSticky={isSticky}
                  currencyISO={portfolioDividend?.Portfolio?.currencyISO}
                  isAnalysisRestricted={
                    portfolioDividend?.Portfolio?.isAnalysisRestricted
                  }
                />
              ))}
              {hasCollapsedHoldings && (
                <tr>
                  <td colSpan={4}>
                    <Button
                      onClick={showAllHoldings}
                      styleType="gentleLarge"
                      display="block"
                    >
                      Show all
                    </Button>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </StyledTable>
      <CtaButton
        marginTop
        as="a"
        href="/discover/investing-ideas/146/dividend-powerhouses-3-yield"
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'discoverTopQuality',
          });
        }}
        styleType="passiveLarge"
      >
        Discover Top Quality Dividend Stocks
        <ChevronRightSmallSolid />
      </CtaButton>
    </ScrollPane>
  );
};
